import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';

import type { DecisionMakersAndLegislativeBodies } from '../../../../../../hooks/dmsWithLegislativeBodies/api';
import { getAllDmsAndLBs } from '../../../../../../hooks/dmsWithLegislativeBodies/api';
import type { NormalizedDMsAndLBs } from '../../../../hooks/useDecisionMakerSection/types';
import { normalizeDmsAndLBs } from '../../../../hooks/useDecisionMakerSection/utils';
import { useTracking } from '../../../../hooks/useTracking';

type Result = ModelHookResult<
	{
		petitionAsk: string;
		totalDmCount: number;
		dmsAndLBs: NormalizedDMsAndLBs;
		loading: boolean;
	},
	{ handleViewAllClick: () => void }
>;

export function useViewAll(): Result {
	const utilityContext = useUtilityContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [allDmsAndLBs, setDmsAndLBs] = useState<DecisionMakersAndLegislativeBodies | undefined>(undefined);

	const {
		data: {
			petition: {
				ask,
				slug,
				dmsWithLegislativeBodiesConnection: { totalCount },
			},
		},
	} = usePetitionDetailsPageData();

	const { trackViewAllDMsClick } = useTracking();

	const getAll = useCallback(async () => {
		setLoading(true);
		try {
			const data = await getAllDmsAndLBs(slug, utilityContext);
			setDmsAndLBs(data.nodes);
		} finally {
			setLoading(false);
		}
	}, [slug, utilityContext]);

	const handleViewAllClick = useCallback(() => {
		trackViewAllDMsClick();
		void getAll();
	}, [trackViewAllDMsClick, getAll]);

	return {
		data: {
			petitionAsk: ask,
			dmsAndLBs: normalizeDmsAndLBs(allDmsAndLBs),
			totalDmCount: totalCount,
			loading,
		},
		actions: { handleViewAllClick },
	};
}
